import { useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button, type ButtonProps } from './button';
import { Textarea } from './textarea';
import { Spinner } from './spinner';

interface BaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ButtonProps['variant'];
  isPending?: boolean;
}

type Props =
  | (BaseProps & { withReason?: false; onConfirm: () => void })
  | (BaseProps & {
      withReason: true;
      reasonLabel?: string;
      reasonRequired?: boolean;
      maxLength?: number;
      onConfirm: (reason: string) => void;
    });

export function ConfirmDialog(props: Props) {
  const {
    open,
    onOpenChange,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'default',
    isPending,
  } = props;

  const [reason, setReason] = useState('');

  const reset = (next: boolean) => {
    if (!next) setReason('');
    onOpenChange(next);
  };

  const handleConfirm = () => {
    if (props.withReason) {
      if (props.reasonRequired !== false && reason.trim().length === 0) return;
      props.onConfirm(reason.trim());
    } else {
      props.onConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {props.withReason ? (
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{props.reasonLabel ?? 'Причина'}</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={props.maxLength ?? 1000}
              rows={4}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {reason.length}/{props.maxLength ?? 1000}
            </p>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => reset(false)} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button variant={variant} onClick={handleConfirm} disabled={isPending}>
            {isPending ? <Spinner /> : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
